import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { blockAccount, getBlocked, unblockAccount } from '../transport/api'
import { shareErrText } from '../transport/relay'
import { Sheet } from '../ui/Sheet'
import { AGENT_SHARES_KEY } from './agentShares'

/**
 * BLOCK — owner, 2026-10-17 (App Store guideline 1.2, relayed by avg-devapp-appstore: 「…封鎖好友的
 * 這一個功能,這樣子上架的時候就可以合規」). One confirm, said the same at every door: where something
 * unwanted ARRIVES (a share offer, a friend request, a friend's agent's gear) and where a friend is
 * managed (the menu's Friends). The list and Unblock live under Friends in the menu.
 */
const BLOCKS_KEY = ['blocks'] as const

/** Every list a block changes — each asks again. */
const TOUCHED = [['friends'], ['friend-requests'], AGENT_SHARES_KEY, BLOCKS_KEY] as const

/** What blocking does, in the one sentence every door shows before it acts. */
function blockSays(name: string): string {
  return (
    `${name} won’t be able to send you a friend request, share an agent with you or message you. ` +
    `Your friendship and every agent either of you shared with the other end now. They aren’t told. ` +
    `You can unblock them from Friends in the menu.`
  )
}

export function BlockConfirm({
  name,
  email,
  account,
  onCancel,
  onBlocked,
  btn = 'mch-btn',
}: {
  name: string
  email: string
  /** The account handle when the screen has one (an offer, a shared agent): one address can be two people. */
  account?: string | undefined
  onCancel: () => void
  onBlocked: () => void
  /** The button style of the screen it sits in. */
  btn?: 'mch-btn' | 'sheet-btn'
}) {
  const qc = useQueryClient()
  const block = useMutation({
    mutationFn: () => blockAccount(email, account),
    onSuccess: () => {
      for (const key of TOUCHED) void qc.invalidateQueries({ queryKey: key })
      onBlocked()
    },
  })
  return (
    <div className="blk-confirm" role="group" aria-label={`Block ${name}`}>
      <p className="blk-says">{blockSays(name)}</p>
      <span className="blk-row">
        <button type="button" className={`${btn} danger`} disabled={block.isPending} onClick={() => block.mutate()}>
          {block.isPending ? 'Blocking…' : `Block ${name}`}
        </button>
        <button type="button" className={btn} disabled={block.isPending} onClick={onCancel}>
          Cancel
        </button>
      </span>
      {block.isError && <p className="gh-note mch-err">{shareErrText(block.error)}</p>}
    </div>
  )
}

/** A row's small "Block", where something unwanted arrived — the confirm opens over it. */
export function BlockDoor({
  name,
  email,
  account,
  className = 'fr-offer-report',
}: {
  name: string
  email: string
  account?: string | undefined
  className?: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)} aria-label={`Block ${name}`}>
        Block
      </button>
      {open && (
        <Sheet title={`Block ${name}?`} onClose={() => setOpen(false)}>
          <BlockConfirm
            name={name}
            email={email}
            account={account}
            onCancel={() => setOpen(false)}
            onBlocked={() => setOpen(false)}
          />
        </Sheet>
      )}
    </>
  )
}

/** The blocker's list, under Friends in the menu — drawn only once there is someone on it. */
export function BlockedSection() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: BLOCKS_KEY, queryFn: getBlocked })
  const unblock = useMutation({
    mutationFn: (account: string) => unblockAccount(account),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: BLOCKS_KEY })
      void qc.invalidateQueries({ queryKey: ['friend-requests'] })
    },
  })
  const rows = q.data?.blocked ?? []
  if (rows.length === 0) return null
  return (
    <>
      <div className="gh-sec">Blocked</div>
      {rows.map((b) => {
        // Read from their own profile. None means deleted — or a year without signing in (a profile's TTL),
        // so the label claims neither (review round 8). Still unblockable by its handle.
        const name = b.name || b.email || 'An account no longer shown'
        return (
          <div className="mch-row blk-item" key={b.account}>
            <span className="mch-meta">
              <span className="mch-name" title={b.email}>
                {name}
              </span>
            </span>
            <button
              type="button"
              className="mch-btn"
              disabled={unblock.isPending}
              onClick={() => unblock.mutate(b.account)}
              aria-label={`Unblock ${name}`}
            >
              Unblock
            </button>
          </div>
        )
      })}
      {unblock.isError && <p className="gh-note mch-err">{shareErrText(unblock.error)}</p>}
      <p className="gh-note">Unblocking lets them ask to be your friend again. Nothing else comes back by itself.</p>
    </>
  )
}
